/**
 * touch-move — pure (Phaser-free) helper for the「拖曳移動」(pointer-follow)
 * touch control. 老闆回饋:平板小孩按不到畫面上的小 d-pad,改成「手指按住畫面,
 * 戰車朝手指位置移動,放開就停」。auto-fire 自己開火,小孩只負責移動。
 *
 * The scene reads `this.input.activePointer` each frame and, when it's down,
 * feeds (curX,curY)→(targetX,targetY) into `stepToward`. We keep the geometry
 * here so it can be unit-tested without booting Phaser.
 *
 * 鐵則:純遊戲操作機制,不碰學習/修煉值。
 */

export interface TouchMoveStep {
  x: number;
  y: number;
}

/**
 * Move (curX,curY) toward (targetX,targetY) by at most `maxStep` px this frame.
 *
 * - If the target is within `deadzone` px (finger basically on the tank), stay
 *   put — returns the original coords. This kills jitter when the kid presses
 *   right on top of the tank.
 * - Otherwise walk along the straight line toward the target by
 *   min(distance, maxStep), so we never overshoot (smooth, frame-rate driven
 *   via maxStep = speed × dtSec) and never teleport.
 *
 * Caller is responsible for clamping the result to the arena bounds
 * (X_MIN/X_MAX/Y_MIN/Y_MAX) — this function is bounds-agnostic.
 *
 * Defensive against non-finite inputs (headless / missing pointer): if any
 * input is not finite, returns the current position unchanged.
 */
export function stepToward(
  curX: number,
  curY: number,
  targetX: number,
  targetY: number,
  maxStep: number,
  deadzone: number,
): TouchMoveStep {
  if (
    !Number.isFinite(curX) || !Number.isFinite(curY) ||
    !Number.isFinite(targetX) || !Number.isFinite(targetY) ||
    !Number.isFinite(maxStep)
  ) {
    return { x: curX, y: curY };
  }

  const dx = targetX - curX;
  const dy = targetY - curY;
  const dist = Math.hypot(dx, dy);

  // Deadzone (and exact-on-target): don't move. Avoids divide-by-zero too.
  const dead = Number.isFinite(deadzone) ? Math.max(0, deadzone) : 0;
  if (dist <= dead || dist === 0) {
    return { x: curX, y: curY };
  }

  // Never step backward / never move a negative amount.
  const step = Math.min(dist, Math.max(0, maxStep));
  if (step === 0) {
    return { x: curX, y: curY };
  }

  const ratio = step / dist;
  return {
    x: curX + dx * ratio,
    y: curY + dy * ratio,
  };
}
